// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/buildRailProgressFromPlan.test.ts
import { type AskPunttAgentStep } from "@mg/schemas/src/christo/askPuntt";

import { type AskV2RailProgressItem } from "./types";

import { ASK_V2_PLAN_TOOL_NAME } from "../planTool";

/** The row id of the placeholder shown before the turn's first plan arrives. */
export const ASK_V2_PLAN_STARTING_ID = "plan-starting";

/**
 * The marker on a step the model withdrew (`cancelled`).
 *
 * A MARKER, not the words on screen: this module is pure and has no i18n, so
 * the hook that owns `t` (`useAskV2PlanProgress`) swaps it for
 * `askV2.rail.progressSteps.skipped`. Rendering it raw left untranslated
 * English in all five non-English locales -- every sibling label on this rail
 * goes through `t`, so this one string was the only one the locale-parity test
 * could not see.
 */
export const ASK_V2_PLAN_SKIPPED_DETAIL = "skipped";

/**
 * The harness statuses (`pydantic_ai_harness.planning.TaskStatus`). `blocked`
 * is unreachable while warhol runs `write_plan` alone -- the toolset refuses it
 * without subtasks -- and is carried anyway so a harness change lands as a row
 * rather than as a dropped step.
 */
const PLAN_STEP_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  "cancelled",
  "blocked",
] as const;

type AskV2PlanStepStatus = (typeof PLAN_STEP_STATUSES)[number];

type AskV2PlanStep = {
  id: string;
  content: string;
  status: AskV2PlanStepStatus;
};

const PLAN_STEP_STATUS_SET: ReadonlySet<string> = new Set(PLAN_STEP_STATUSES);
// These are the rejection producers, not display copy: the harness returns
// `Plan not updated:` (pinned in warhol's test_plan.py) and Christo's argument
// validator returns `Arguments rejected:` from warholChat.ts.
const REJECTED_PLAN_RESULT_PREFIXES = [
  "Plan not updated:",
  "Arguments rejected:",
] as const;

function isRejectedPlanResult(summary: unknown): boolean {
  return (
    typeof summary === "string" &&
    REJECTED_PLAN_RESULT_PREFIXES.some((prefix) => summary.startsWith(prefix))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * The plan a `write_plan` call carries, or null when the args are not one.
 *
 * `write_plan` takes `items: list[PlanItem]` and the model passes the WHOLE
 * plan on every call, so one call is a complete snapshot -- there is nothing to
 * merge with the call before it.
 */
function planStepsFromArgs(args: unknown): AskV2PlanStep[] | null {
  if (!isRecord(args)) return null;
  const items = args.items;
  if (!Array.isArray(items)) return null;

  const steps: AskV2PlanStep[] = [];
  for (const item of items) {
    if (!isRecord(item)) continue;
    const content = stringField(item.content);
    if (!content) continue;
    const status = stringField(item.status);
    steps.push({
      // `PlanItem.id` is auto-generated by the harness, but a plan the model
      // wrote without ids still has to key a row; the index covers it.
      id: stringField(item.id) || `plan-step-${steps.length}`,
      content,
      status: PLAN_STEP_STATUS_SET.has(status)
        ? (status as AskV2PlanStepStatus)
        : "pending",
    });
  }
  return steps.length > 0 ? steps : null;
}

/**
 * The step marker the harness renders, mapped back onto its status.
 *
 * `[x]` done, `[~]` in progress, `[ ]` still to come. Anything else (a marker a
 * newer harness adds) reads as `pending`, which claims nothing.
 */
const PLAN_SUMMARY_STATUS: Record<string, AskV2PlanStepStatus> = {
  x: "completed",
  "~": "in_progress",
  " ": "pending",
};

/** `1. [x] Run selected review checks` -- the harness's rendered plan line. */
const PLAN_SUMMARY_LINE = /^\s*\d+\.\s*\[(.)\]\s+(\S.*)$/;

/**
 * The plan off the tool's RENDERED return, or null when it is not one.
 *
 * The same five steps the `tool_call` carries in `args`, as the harness prints
 * them back (shot2 of the Story 4 feedback: `Plan updated: 5 step(s).` then
 * `1. [x] ...` per line, then `(3/5 completed)`).
 *
 * It exists because `args` is not the only shape the plan arrives in. christo
 * builds a turn's steps from the running turn's Temporal history, and
 * `warholAgentSteps` pushes a `tool_result` slot for every settled call
 * whether or not it saw that call's own event -- `if (slot.call)` then
 * `if (slot.result)`, each independent. A call whose `function_tool_call`
 * event fell outside the paginated read therefore reaches the rail as a lone
 * result: no `args`, and the whole plan sitting in `summary`. Reading `args`
 * alone drew "Getting started" over a review that had written its checklist.
 *
 * The trailing budget line (`[budget: 59 of 64 tool calls left]`) does not
 * match the line pattern, so it is neither a step nor able to shift the count.
 */
export function planStepsFromSummary(summary: unknown): AskV2PlanStep[] | null {
  if (typeof summary !== "string" || !summary) return null;

  const steps: AskV2PlanStep[] = [];
  for (const line of summary.split("\n")) {
    const match = PLAN_SUMMARY_LINE.exec(line);
    if (!match) continue;
    const content = (match[2] ?? "").trim();
    if (!content) continue;
    steps.push({
      id: `plan-step-${steps.length}`,
      content,
      status: PLAN_SUMMARY_STATUS[match[1] ?? ""] ?? "pending",
    });
  }
  return steps.length > 0 ? steps : null;
}

/**
 * The steps of the LATEST `write_plan` call in the turn, or null when the turn
 * has not written one yet.
 *
 * Later calls supersede earlier ones because the tool is whole-plan
 * replacement: a row that followed the first call would keep showing a step the
 * model has since rewritten or dropped.
 *
 * Structured args are preferred for a call because they are the plan as the
 * model wrote it, statuses included and never truncated. The rendered summary
 * is only a fallback when that call's args were not persisted. When a rejection
 * result is present in the history slice, its call is paired by `stepId` or by
 * unkeyed call/result order and cannot replace the last accepted snapshot. A
 * lone call whose result fell outside the slice is indistinguishable from an
 * accepted call.
 */
export function readLatestPlanSteps(
  agentSteps: readonly AskPunttAgentStep[] | undefined,
): AskV2PlanStep[] | null {
  if (!agentSteps?.length) return null;

  // A write_plan call can carry perfectly parseable items and still be refused
  // by the tool (for example, duplicate ids). Pair calls/results before reading
  // the timeline so a rejected call cannot briefly become canonical.
  const rejectedCallIds = new Set<string>();
  const rejectedUnkeyedCallIndexes = new Set<number>();
  const callIdsWithStructuredArgs = new Set<string>();
  const unkeyedResultIndexesWithStructuredArgs = new Set<number>();
  let pendingUnkeyedStructuredCallIndex: number | null = null;
  for (let index = 0; index < agentSteps.length; index += 1) {
    const step = agentSteps[index];
    if (!step) continue;
    if (step.type === "tool_call" && step.tool === ASK_V2_PLAN_TOOL_NAME) {
      const parsed = planStepsFromArgs(step.args);
      const stepId = step.stepId || null;
      if (stepId && parsed) callIdsWithStructuredArgs.add(stepId);
      if (!stepId) pendingUnkeyedStructuredCallIndex = parsed ? index : null;
      continue;
    }
    if (step.type === "tool_result" && step.tool === ASK_V2_PLAN_TOOL_NAME) {
      const stepId = step.stepId || null;
      if (!stepId && pendingUnkeyedStructuredCallIndex != null) {
        unkeyedResultIndexesWithStructuredArgs.add(index);
      }
      if (isRejectedPlanResult(step.summary)) {
        if (stepId) rejectedCallIds.add(stepId);
        else if (pendingUnkeyedStructuredCallIndex != null) {
          rejectedUnkeyedCallIndexes.add(pendingUnkeyedStructuredCallIndex);
        }
      }
      if (!stepId) pendingUnkeyedStructuredCallIndex = null;
    }
  }

  let latest: AskV2PlanStep[] | null = null;
  for (let index = 0; index < agentSteps.length; index += 1) {
    const step = agentSteps[index];
    if (!step) continue;
    if (step.type === "tool_call") {
      const stepId = step.stepId || null;
      if (
        step.tool !== ASK_V2_PLAN_TOOL_NAME ||
        (stepId != null && rejectedCallIds.has(stepId)) ||
        (stepId == null && rejectedUnkeyedCallIndexes.has(index))
      ) {
        continue;
      }
      const parsed = planStepsFromArgs(step.args);
      if (parsed) latest = parsed;
      continue;
    }
    if (step.type === "tool_result") {
      const stepId = step.stepId || null;
      if (
        step.tool !== ASK_V2_PLAN_TOOL_NAME ||
        isRejectedPlanResult(step.summary) ||
        (stepId != null && rejectedCallIds.has(stepId)) ||
        (stepId != null && callIdsWithStructuredArgs.has(stepId)) ||
        (stepId == null && unkeyedResultIndexesWithStructuredArgs.has(index))
      ) {
        continue;
      }
      const parsed = planStepsFromSummary(step.summary);
      if (parsed) latest = parsed;
    }
  }
  return latest;
}

/**
 * How far through its plan the turn says it is, or null when it has no plan.
 *
 * Counted off the plan's own statuses, so the line agrees with the rows above
 * it by construction: a step the model marked `completed` is a ticked row and a
 * counted step, and one it withdrew is neither. The harness's own rendered
 * return counts the same way (`(3/5 completed)`), which is the line this
 * replaces on the rail.
 */
export function planProgressTally(
  steps: readonly AskV2PlanStep[] | null,
): { completed: number; total: number } | null {
  if (!steps?.length) return null;
  return {
    completed: steps.filter((step) => step.status === "completed").length,
    total: steps.length,
  };
}

/**
 * The newest plan in the messages given, or null when none of them carries one.
 *
 * Give it ONE review cycle, not a whole thread: the caller
 * (`useAskV2PlanProgress`) slices from the latest Start review first, because
 * the newest plan in a thread belongs to the previous review whenever the
 * running turn has not written its own yet — and showing that one ticks rows
 * for work the new review has not done.
 */
export function readLatestPlanStepsFromMessages(
  messages: readonly { agentSteps?: AskPunttAgentStep[] }[] | undefined,
): AskV2PlanStep[] | null {
  let latest: AskV2PlanStep[] | null = null;
  for (const message of messages ?? []) {
    const parsed = readLatestPlanSteps(message.agentSteps);
    if (parsed) latest = parsed;
  }
  return latest;
}

function rowStatus(
  status: AskV2PlanStepStatus,
): AskV2RailProgressItem["status"] {
  switch (status) {
    case "completed":
      return "done";
    case "in_progress":
      return "active";
    case "cancelled":
      // Stays on the rail with its label: a step the model withdrew is a thing
      // that happened to the review, and hiding it would make the row count
      // disagree with the plan the model is working from.
      return "done";
    case "blocked":
      // Nothing in warhol blocks a step today; a blocked row that read "done"
      // would claim work that did not happen.
      return "active";
    default:
      return "pending";
  }
}

/** One rail row per plan step, in the order the model wrote them. */
export function planStepsToProgressItems(
  steps: readonly AskV2PlanStep[],
): AskV2RailProgressItem[] {
  return steps.map((step) => ({
    id: `plan-${step.id}`,
    label: step.content,
    ...(step.status === "cancelled"
      ? { detail: ASK_V2_PLAN_SKIPPED_DETAIL }
      : {}),
    status: rowStatus(step.status),
  }));
}

/**
 * Put the turn's "k/n completed" line on the row it belongs to.
 *
 * The step being worked on, or the last one when nothing is active (a plan the
 * model has not advanced yet, and a finished review whose rows are all done).
 * A row that already carries a `detail` keeps its own: the withdrawn step's
 * `skipped` marker says something the count cannot, and overwriting it traded
 * one fact for a less true one.
 */
function attachProgressDetail(
  items: readonly AskV2RailProgressItem[],
  detail: string | undefined,
): AskV2RailProgressItem[] {
  if (!detail || items.length === 0) return [...items];
  const activeIndex = items.findIndex((item) => item.status === "active");
  const target = activeIndex >= 0 ? activeIndex : items.length - 1;
  return items.map((item, index) =>
    index === target && !item.detail ? { ...item, detail } : item,
  );
}

/**
 * The rows a warhol turn's rail shows.
 *
 * `null` plan means the turn has not called `write_plan` yet -- a turn that
 * needs one tool call is never planned, and the model may still be deciding.
 * The caller decides what to show for that (one honest "getting started" row);
 * this function never invents a step.
 *
 * `progressDetail` is the turn's "k/n completed" line, already formatted by the
 * caller that owns the catalog (`useAskV2PlanProgress`): this module is pure
 * and has no i18n.
 */
export function buildAskV2PlanProgress(input: {
  steps: readonly AskV2PlanStep[] | null;
  completed: boolean;
  progressDetail?: string;
}): AskV2RailProgressItem[] {
  if (!input.steps) return [];
  const items = planStepsToProgressItems(input.steps);
  // A finished review has no active step; the plan is kept so the person can
  // still read what the review did.
  const rows = input.completed
    ? items.map((item) => ({ ...item, status: "done" as const }))
    : items;
  return attachProgressDetail(rows, input.progressDetail);
}
